import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DataTable from '../components/DataTable';
import './DatabasePage.css';
import axios from 'axios';
import { getUserId, getCurrentUser } from '../utils/auth';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import ArticleCard from '../components/ArticleCard';
import DatabaseViewMenu from '../components/DatabaseViewMenu';
import '../components/ArticleCard.css';
import { API, readArticleService, databaseFiltersService } from '../services/api';
import ArticleDetail from '../components/ArticleDetail';
import DatabaseFilterModal from '../components/DatabaseFilterModal';
import Toast from '../components/Toast';
import WelcomeModal from '../components/WelcomeModal';
import PatentUpgradeModal from '../components/PatentUpgradeModal';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { FilterPreferencesContext } from '../App';
import { useFilters } from '../context/FiltersContext';

/* ------------------ helpers ------------------ */
const toUniqueStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => String(v))
    .filter((v) => (seen.has(v) ? false : (seen.add(v), true)));
};

const sanitizeOptionValues = (values) => {
  const set = new Set();
  (Array.isArray(values) ? values : [])
    .map((value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    })
    .filter((value) => value.length > 0)
    .forEach((value) => set.add(value));
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
};

const UI_STORAGE_KEYS = {
  appliedFilters: 'database.appliedFilters'
};

const DENSITY_STORAGE_KEY = 'database.density';

const sanitizeDatabaseFilters = (raw = {}) => {
  const base = raw && typeof raw === 'object' ? raw : {};
  const subsystemSource = [
    ...(Array.isArray(base.subsystem) ? base.subsystem : []),
    ...(Array.isArray(base.Subsystem) ? base.Subsystem : []),
  ];

  const yearValues = Array.isArray(base.year_of_publication) ? base.year_of_publication : [];
  const parsedYear = yearValues
    .map((v) => Number.parseInt(v, 10))
    .filter((v) => Number.isFinite(v));
  let yearRange = [];
  if (parsedYear.length === 2) {
    const [a, b] = parsedYear;
    yearRange = a <= b ? [a, b] : [b, a];
  }

  return {
    OEM: toUniqueStringArray(base.OEM),
    brand: toUniqueStringArray(base.brand),
    car_model: toUniqueStringArray(base.car_model),
    system: toUniqueStringArray(base.system),
    subsystem: toUniqueStringArray(subsystemSource),
    part: toUniqueStringArray(base.part),
    type: toUniqueStringArray(base.type),
    material: toUniqueStringArray(base.material),
    technology: toUniqueStringArray(base.technology),
    year_of_publication: yearRange,
    status: typeof base.status === 'string' ? (base.status === 'new' ? 'unread' : base.status) : '',
    query: typeof base.query === 'string' ? base.query : '',
  };
};

const EMPTY_FILTERS = sanitizeDatabaseFilters({});

const areFiltersEmpty = (filters) => {
  if (!filters) {
    return true;
  }
  const {
    OEM = [],
    brand = [],
    car_model = [],
    system = [],
    subsystem = [],
    part = [],
    type = [],
    material = [],
    technology = [],
    year_of_publication = [],
    status = '',
    query = ''
  } = filters;

  return (
    OEM.length === 0 &&
    brand.length === 0 &&
    car_model.length === 0 &&
    system.length === 0 &&
    subsystem.length === 0 &&
    part.length === 0 &&
    type.length === 0 &&
    material.length === 0 &&
    technology.length === 0 &&
    year_of_publication.length === 0 &&
    status === '' &&
    query === ''
  );
};
const normalizeFiltersForCompare = (filters = {}) => ({
  OEM: Array.isArray(filters?.OEM) ? filters.OEM : [],
  brand: Array.isArray(filters?.brand) ? filters.brand : [],
  car_model: Array.isArray(filters?.car_model) ? filters.car_model : [],
  system: Array.isArray(filters?.system) ? filters.system : [],
  subsystem: Array.isArray(filters?.subsystem) ? filters.subsystem : [],
  part: Array.isArray(filters?.part) ? filters.part : [],
  type: Array.isArray(filters?.type) ? filters.type : [],
  material: Array.isArray(filters?.material) ? filters.material : [],
  technology: Array.isArray(filters?.technology) ? filters.technology : [],
  year_of_publication: Array.isArray(filters?.year_of_publication) ? filters.year_of_publication : [],
  status: typeof filters?.status === 'string' ? filters.status : '',
  query: typeof filters?.query === 'string' ? filters.query : ''
});

const areFiltersEqual = (a, b) => {
  return JSON.stringify(normalizeFiltersForCompare(a)) === JSON.stringify(normalizeFiltersForCompare(b));
};


const buildDatabaseSearchParams = (filters) => {
  const params = new URLSearchParams();
  if (!filters) return params;

  const appendArray = (key, values) => {
    if (Array.isArray(values) && values.length > 0) values.forEach((v) => params.append(key, v));
  };

  appendArray('OEM', filters.OEM);
  appendArray('brand', filters.brand);
  appendArray('car_model', filters.car_model);
  appendArray('System', filters.system);
  appendArray('Subsystem', filters.subsystem);
  appendArray('part', filters.part);
  appendArray('Type', filters.type);
  appendArray('material', filters.material);
  appendArray('technology', filters.technology);

  if (Array.isArray(filters.year_of_publication) && filters.year_of_publication.length === 2) {
    params.set('year_min', filters.year_of_publication[0]);
    params.set('year_max', filters.year_of_publication[1]);
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.query) params.set('query', filters.query);
  return params;
};

/* ------------------ page ------------------ */
const DatabasePage = ({ viewMode = 'list', onViewModeChange, tagPreference = 'with-tags', tagStylePreference = 'colored' }) => {
  const outletContext = useOutletContext() || {};
  const effectiveTagPreference = outletContext.tagPreference ?? tagPreference;
  const effectiveTagStylePreference = outletContext.tagStylePreference ?? tagStylePreference;
  const setHeaderInfo = outletContext.setHeaderInfo;
  const location = useLocation();
  const navigate = useNavigate();
  const { updateFilterOptions: updateContextFilterOptions, updateActiveFilters: updateContextActiveFilters, registerFilterCallbacks } = useFilters();

  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [favorites, setFavorites] = useState({});
  const [readLaterItems, setReadLaterItems] = useState({});
  const [toast, setToast] = useState(null);
  const [showPatentUpgradeModal, setShowPatentUpgradeModal] = useState(false);
  const [cardViewSortConfig, setCardViewSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') {
      return 'detailed';
    }
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored === 'compact' ? 'compact' : 'detailed';
  });
  const displayTags = useMemo(() => {
    const enabled = effectiveTagPreference !== 'without-tags';
    const value = enabled;
    return {
      OEM: value,
      brand: value,
      car_model: value,
      part: value,
      System: value,
      Type: value,
      Material: value,
      technology: value,
      subsystem: value,
    };
  }, [effectiveTagPreference]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [newArticlesCount, setNewArticlesCount] = useState(0);
  const [userName, setUserName] = useState('');
  const [readArticles, setReadArticles] = useState({});
  const filtersWrapperRef = useRef(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [isSavingCurrentFilter, setIsSavingCurrentFilter] = useState(false);
  const [defineModalOpen, setDefineModalOpen] = useState(false);
  const [savingDefinedFilter, setSavingDefinedFilter] = useState(false);
  const [defineModalError, setDefineModalError] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }
  }, [density]);

  // sticky height
  useEffect(() => {
    const root = document.documentElement;
    const updateHeight = () => {
      const node = filtersWrapperRef.current;
      if (!node) return root.style.setProperty('--database-filters-sticky-height', '0px');
      const h = node.getBoundingClientRect().height;
      root.style.setProperty('--database-filters-sticky-height', `${Math.round(h)}px`);
    };
    updateHeight();
    let obs;
    const node = filtersWrapperRef.current;
    if (typeof ResizeObserver !== 'undefined' && node) {
      obs = new ResizeObserver(updateHeight);
      obs.observe(node);
    } else {
      window.addEventListener('resize', updateHeight);
    }
    return () => {
      if (obs && node) {
        obs.unobserve(node);
        obs.disconnect();
      } else {
        window.removeEventListener('resize', updateHeight);
      }
      root.style.setProperty('--database-filters-sticky-height', '0px');
    };
  }, []);
  useEffect(() => {
    const node = filtersWrapperRef.current;
    if (!node) return;

    const updateHeight = () => {
      const h = node.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--database-filters-sticky-height', `${Math.round(h)}px`);
    };

    // Use multiple RAF frames to ensure layout is complete
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(updateHeight);
    });

    // Also update after a short delay to catch fully rendered FilterPanel
    const timeoutId = setTimeout(updateHeight, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  // url <-> state
  const getFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return {
      OEM: params.getAll('OEM'),
      brand: params.getAll('brand'),
      car_model: params.getAll('car_model'),
      system: params.getAll('System'),
      type: params.getAll('Type'),
      material: params.getAll('material'),
      technology: params.getAll('technology'),
      subsystem: [...params.getAll('Subsystem'), ...params.getAll('subsystem')],
      part: params.getAll('part'),
      year_of_publication:
        params.get('year_min') && params.get('year_max')
          ? [parseInt(params.get('year_min'), 10), parseInt(params.get('year_max'), 10)]
          : [],
      status: params.get('status') || '',
      query: params.get('query') || '',
    };
  };

  const [filters, setFilters] = useState(() => {
    const fromUrl = sanitizeDatabaseFilters(getFiltersFromUrl());
    if (!areFiltersEmpty(fromUrl)) {
      return fromUrl;
    }

    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(UI_STORAGE_KEYS.appliedFilters);
        if (stored) {
          const parsed = JSON.parse(stored);
          const sanitizedStored = sanitizeDatabaseFilters(parsed);
          if (!areFiltersEmpty(sanitizedStored)) {
            return sanitizedStored;
          }
        }
      } catch (error) {
        console.error('Unable to read saved filters :', error);
      }
    }

    return fromUrl;
  });
  const filtersRef = useRef(filters);

  const [filterOptions, setFilterOptions] = useState({
    OEM: [],
    brand: [],
    car_model: [],
    system: ['BIW', 'Chassis', 'Battery'],
    type: ['Linkedin', 'Patent', 'Article'],
    material: [],
    technology: [],
    subsystem: [],
    part: [],
    year_of_publication: [],
  });

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (areFiltersEmpty(filters)) {
        window.localStorage.removeItem(UI_STORAGE_KEYS.appliedFilters);
      } else {
        window.localStorage.setItem(UI_STORAGE_KEYS.appliedFilters, JSON.stringify(filters));
      }
    } catch (error) {
      console.error('Unable to save applied filters:', error);
    }
  }, [filters]);

  // Sync filterOptions to FiltersContext
  useEffect(() => {
    updateContextFilterOptions('database', filterOptions);
  }, [filterOptions, updateContextFilterOptions]);

  // Sync active filters to FiltersContext
  useEffect(() => {
    updateContextActiveFilters('database', filters);
  }, [filters, updateContextActiveFilters]);

  // Update header info with results count and active filters
  useEffect(() => {
    if (!setHeaderInfo) return;

    const resultsText = articles.length > 0
      ? `Results 1-${articles.length} of ${articles.length}`
      : 'No results found';

    const activeFiltersList = [];

    // Map filter keys to their display labels
    const filterLabels = {
      OEM: 'OEM',
      brand: 'Brand',
      car_model: 'Car Model',
      system: 'System',
      subsystem: 'Subsystem',
      part: 'Part',
      type: 'Type',
      material: 'Material',
      technology: 'Technology'
    };

    // Build active filters list
    Object.entries(filters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          activeFiltersList.push({
            key: `${key}-${value}`,
            filterKey: key,
            label: filterLabels[key] || key,
            value: value
          });
        });
      }
    });

    setHeaderInfo({
      resultsText,
      activeFilters: activeFiltersList
    });

    // Cleanup on unmount
    return () => {
      if (setHeaderInfo) {
        setHeaderInfo({
          resultsText: '',
          activeFilters: []
        });
      }
    };
  }, [articles, filters, setHeaderInfo]);

  const syncFiltersToUrl = useCallback(
    (next) => {
      const params = buildDatabaseSearchParams(next);
      navigate({ search: params.toString() }, { replace: true });
    },
    [navigate]
  );

  const refreshData = () => setRefreshTrigger((p) => p + 1);

  /* -------- saved filters -------- */
  const loadSavedFilters = useCallback(async () => {
    try {
      const response = await databaseFiltersService.getAll();
      if (Array.isArray(response?.filters)) setSavedFilters(response.filters);
      else if (Array.isArray(response)) setSavedFilters(response);
      else setSavedFilters([]);
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
  }, []);
  useEffect(() => { loadSavedFilters(); }, [loadSavedFilters]);

  const showToast = useCallback((message, type = 'success') => {
    console.log('showToast called:', message, type);
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const updateFilter = useCallback((filterName, value) => {
    const normalizedName = filterName === 'Subsystem' ? 'subsystem' : filterName;
    setFilters((currentFilters) => {
      const next = sanitizeDatabaseFilters({ ...currentFilters, [normalizedName]: value });
      syncFiltersToUrl(next);
      return next;
    });
  }, [syncFiltersToUrl]);

  const resetAllFilters = useCallback(() => {
    const empty = sanitizeDatabaseFilters({});
    setFilters(empty);
    syncFiltersToUrl(empty);
  }, [syncFiltersToUrl]);

  const handleRemoveFilter = (key, value) => {
    const arr = Array.isArray(filters[key]) ? filters[key].filter((v) => v !== value) : [];
    updateFilter(key, arr);
  };

  const handleSaveCurrentFilters = useCallback(async () => {
    if (isSavingCurrentFilter) return;
    const name = typeof window !== 'undefined' ? window.prompt('Name your filter') : '';
    if (!name || !name.trim()) return;

    setIsSavingCurrentFilter(true);
    try {
      const res = await databaseFiltersService.save(name.trim(), filters);
      if (res?.success) {
        showToast('Filter saved', 'success');
        await loadSavedFilters();
      } else showToast('Unable to save this filter', 'error');
    } catch (e) {
      console.error('save filter error:', e);
      if (e?.response?.status === 409) showToast('A filter with this name already exists.', 'error');
      else showToast('Error saving filter.', 'error');
    } finally {
      setIsSavingCurrentFilter(false);
    }
  }, [isSavingCurrentFilter, filters, loadSavedFilters, showToast]);

  // Register filter callbacks in FiltersContext (must be after all callbacks are defined)
  useEffect(() => {
    registerFilterCallbacks('database', {
      onFilterChange: updateFilter,
      onResetAllFilters: resetAllFilters,
      onSaveCurrentFilters: handleSaveCurrentFilters,
      savingCurrentFilter: isSavingCurrentFilter,
    });
  }, [registerFilterCallbacks, updateFilter, resetAllFilters, handleSaveCurrentFilters, isSavingCurrentFilter]);

  const handleOpenDefineFilter = () => { setDefineModalError(''); setDefineModalOpen(true); };
  const handleCloseDefineFilter = () => { if (!savingDefinedFilter) { setDefineModalOpen(false); setDefineModalError(''); } };

  const handleSaveDefinedFilter = async ({ name, filters: preset }) => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed) return setDefineModalError('Please enter a filter name.');
    const payload = sanitizeDatabaseFilters(preset);
    setSavingDefinedFilter(true);
    try {
      const res = await databaseFiltersService.save(trimmed, payload);
      if (res?.success) {
        showToast('Filter saved', 'success');
        setDefineModalOpen(false);
        setDefineModalError('');
        await loadSavedFilters();
      } else setDefineModalError('Unable to save this filter.');
    } catch (e) {
      console.error('save defined filter error:', e);
      if (e?.response?.status === 409) setDefineModalError('A filter with this name already exists.');
      else setDefineModalError('Error saving filter.');
    } finally {
      setSavingDefinedFilter(false);
    }
  };

  const handleApplySavedFilter = (doc) => {
    if (!doc) return;
    const payload = doc.filters && typeof doc.filters === 'object' ? doc.filters : doc;
    const sanitized = sanitizeDatabaseFilters(payload);
    setFilters(sanitized);
    syncFiltersToUrl(sanitized);
    showToast(doc.name ? `Filter ${doc.name} applied` : 'Filter applied', 'success');
  };

  const handleRequestRemoveSavedFilter = async (doc) => {
    if (!doc) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete filter "${doc?.name || 'Unnamed'}"?`)
      : true;
    if (!ok) return;
    try {
      await databaseFiltersService.remove(doc._id || doc.id);
      showToast('Filter removed', 'success');
      await loadSavedFilters();
    } catch (e) {
      console.error('remove filter error:', e);
      showToast('Unable to remove this filter.', 'error');
    }
  };

  const handleViewMenuChange = (value) => {
    if (value === 'card') {
      if (typeof onViewModeChange === 'function') {
        onViewModeChange('card');
      }
      return;
    }
    const nextDensity = value === 'compact' ? 'compact' : 'detailed';
    setDensity(nextDensity);
    if (typeof onViewModeChange === 'function') {
      onViewModeChange('list');
    }
  };

  const renderSavedFiltersPanel = () => {
    return (
      <div className="database-saved-filters-panel">
        <div className="database-saved-filters-panel__header">
          {Array.isArray(savedFilters) && savedFilters.length > 0 && (
            <>
              <span className="database-saved-filters-panel__title">{`Saved Filters${savedFilters.length ? ` (${savedFilters.length})` : ''}:`}</span>
              <div className="database-saved-filters-panel__list">
                {savedFilters.map((doc) => {
                  const key = doc?._id || doc?.id || doc?.name;
                  if (!key) return null;
                  const name = doc?.name || 'Sans nom';
                  const onKey = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleApplySavedFilter(doc);
                    }
                  };
                  return (
                    <span
                      key={key}
                      className="database-filter-chip database-saved-filter-chip"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleApplySavedFilter(doc)}
                      onKeyDown={onKey}
                    >
                      <span className="database-filter-chip-label">Filter</span>
                      <span className="database-filter-chip-value">{name}</span>
                      <button
                        type="button"
                        className="filter-chip-remove database-saved-filter-chip__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestRemoveSavedFilter(doc);
                        }}
                        aria-label={`Supprimer le filtre ${name}`}
                      >
                        &times;
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <div className="database-saved-filters-panel__actions">
            <div className="database-sort">
              <label htmlFor="sort-select" className="database-sort-label">Sort by</label>
              <select
                id="sort-select"
                className="database-sort-select"
                value={`${cardViewSortConfig.key}:${cardViewSortConfig.direction}`}
                onChange={handleSortChange}
              >
                <option value="createdAt:descending">Most recent</option>
                <option value="createdAt:ascending">Oldest</option>
                <option value="Title:ascending">Title A-Z</option>
              </select>
            </div>
            <DatabaseViewMenu
              selected={viewMode === 'card' ? 'card' : density}
              onSelect={handleViewMenuChange}
            />
          </div>
        </div>
      </div>
    );
  };

  /* -------- filter options (stats + fallback json) -------- */
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await API.get('/articles/stats');
        if (response.data?.success) {
          const av = response.data.availableValues || {};
          setFilterOptions({
            OEM: sanitizeOptionValues(av.OEM),
            brand: sanitizeOptionValues(av.brand),
            car_model: sanitizeOptionValues(av.car_model),
            system: sanitizeOptionValues(av.System),
            type: sanitizeOptionValues(av.Type),
            material: sanitizeOptionValues(av.material),
            technology: sanitizeOptionValues(av.technology),
            subsystem: sanitizeOptionValues([
              ...(Array.isArray(av.subsystem) ? av.subsystem : []),
              ...(Array.isArray(av.Subsystem) ? av.Subsystem : []),
            ]),
            part: sanitizeOptionValues(av.part),
            year_of_publication: sanitizeOptionValues(av.year_of_publication).length
              ? sanitizeOptionValues(av.year_of_publication)
              : [],
          });
        }
        // fallback local
        fetch('/list_criteria_fixed.json')
          .then((r) => r.json())
          .then((data) => {
            const crit = Array.isArray(data?.criteres) ? data.criteres : [];
            const pick = (name) => crit.find((c) => c.nom === name)?.valeurs || [];
            setFilterOptions((prev) => ({
              ...prev,
              OEM: prev.OEM?.length ? prev.OEM : sanitizeOptionValues(pick('OEM')),
              subsystem: prev.subsystem?.length ? prev.subsystem : sanitizeOptionValues(pick('Subsystem')),
            }));
          })
          .catch((err) => console.error('Erreur chargement critres locaux:', err));
      } catch (err) {
        console.error('Erreur chargement options de filtrage:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // sync when URL changes
  useEffect(() => {
    const fromUrl = sanitizeDatabaseFilters(getFiltersFromUrl());
    const currentFilters = filtersRef.current;

    if (location.search.length === 0 && !areFiltersEmpty(currentFilters)) {
      return;
    }

    if (!areFiltersEqual(currentFilters, fromUrl)) {
      setFilters(fromUrl);
    }
  }, [location.search]);

  /* -------- articles -------- */
  useEffect(() => {
    const fetchArticles = async () => {
      setIsLoading(true);
      try {
        console.log('🔍 Frontend filters.brand:', filters.brand);
        let url = '/articles';
        const qp = [];

        if (filters.OEM?.length) filters.OEM.forEach((v) => qp.push(`OEM=${encodeURIComponent(v)}`));
        if (filters.brand?.length) filters.brand.forEach((v) => qp.push(`brand=${encodeURIComponent(v)}`));
        if (filters.car_model?.length) filters.car_model.forEach((v) => qp.push(`car_model=${encodeURIComponent(v)}`));
        if (filters.system?.length) filters.system.forEach((v) => qp.push(`System=${encodeURIComponent(v)}`));
        if (filters.subsystem?.length) filters.subsystem.forEach((v) => qp.push(`Subsystem=${encodeURIComponent(v)}`));
        if (filters.part?.length) filters.part.forEach((v) => qp.push(`part=${encodeURIComponent(v)}`));
        if (filters.type?.length) filters.type.forEach((v) => qp.push(`Type=${encodeURIComponent(v)}`));
        if (filters.material?.length) filters.material.forEach((v) => qp.push(`material=${encodeURIComponent(v)}`));
        if (filters.technology?.length) filters.technology.forEach((v) => qp.push(`technology=${encodeURIComponent(v)}`));
        if (filters.year_of_publication?.length === 2) {
          qp.push(`year_min=${filters.year_of_publication[0]}`);
          qp.push(`year_max=${filters.year_of_publication[1]}`);
        }
        if (filters.query) qp.push(`query=${encodeURIComponent(filters.query)}`);

        const userId = getUserId();
        if (userId) qp.push(`userId=${encodeURIComponent(userId)}`);
        qp.push(`_t=${Date.now()}`);
        if (qp.length) url += '?' + qp.join('&');

        console.log('🔍 Frontend fetching URL:', url);
        const response = await API.get(url);
        if (response.data?.success) {
          const articlesPayload = Array.isArray(response.data.data) ? response.data.data : [];
          setArticles(articlesPayload);

          const brandsFromArticles = sanitizeOptionValues(articlesPayload.map((article) => article?.brand));
          const carModelsFromArticles = sanitizeOptionValues(articlesPayload.map((article) => article?.car_model));
          const subsystemsFromArticles = sanitizeOptionValues(articlesPayload.flatMap((article) => [article?.subsystem, article?.Subsystem]));
          const partsFromArticles = sanitizeOptionValues(articlesPayload.map((article) => article?.part));
          const technologiesFromArticles = sanitizeOptionValues(articlesPayload.map((article) => article?.technology));

          setFilterOptions((prev) => ({
            ...prev,
            subsystem: subsystemsFromArticles.length > 0 ? subsystemsFromArticles : prev.subsystem,
          }));

          setError(null);
        } else setError('Erreur lors du chargement des articles');
      } catch (err) {
        console.error('Erreur chargement articles:', err);
        setError('Erreur de connexion au serveur');
      } finally {
        setIsLoading(false);
      }
    };
    fetchArticles();
  }, [filters, refreshTrigger]);

  // read articles
  useEffect(() => {
    (async () => {
      try {
        const readIds = await readArticleService.getReadArticles();
        const map = {};
        (Array.isArray(readIds) ? readIds : []).forEach((id) => {
          const key = String(id);
          map[key] = true;
        });
        setReadArticles(map);
      } catch (e) {
        console.error('Erreur chargement articles lus:', e);
      }
    })();
  }, []);

  const handleMarkAsRead = async (id, legacyId) => {
    const primaryId = id ?? legacyId;
    if (primaryId === undefined || primaryId === null) {
      return;
    }

    const key = String(primaryId);
    setReadArticles((prev) => ({ ...prev, [key]: true }));
    updateArticleState(primaryId, { isRead: true });

    try {
      await readArticleService.markAsRead(id ?? legacyId, legacyId ?? id);
    } catch (e) {
      console.error('Erreur markAsRead:', e);
      setReadArticles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      updateArticleState(primaryId, { isRead: false });
    }
  };

  const updateArticleState = (id, changes) => {
    setArticles((prev) =>
      prev.map((a) => ((a.ID || a._id) === id ? { ...a, ...changes } : a))
    );
  };

  // Handle favorite toggle from card
  const handleFavoriteToggle = async (article) => {
    const articleId = article.ID || article._id;
    const userId = getUserId();
    const currentFavoriteState = favorites[articleId] || article.isFavorite;
    const newFavoriteState = !currentFavoriteState;

    // Optimistic update
    setFavorites((prev) => ({ ...prev, [articleId]: newFavoriteState }));
    updateArticleState(articleId, { isFavorite: newFavoriteState });

    try {
      if (currentFavoriteState) {
        await API.delete(`/favorites/${article.ID}`, {
          params: { userId }
        });
      } else {
        await API.post('/favorites', {
          userId,
          articleId: article.ID
        });
      }
      showToast(newFavoriteState ? 'Added to favorites' : 'Removed from favorites', 'success');
    } catch (error) {
      // Revert on error
      setFavorites((prev) => ({ ...prev, [articleId]: currentFavoriteState }));
      updateArticleState(articleId, { isFavorite: currentFavoriteState });
      showToast('Error updating favorites', 'error');
    }
  };

  // Handle reading list toggle from card
  const handleReadLaterToggle = async (article) => {
    const articleId = article.ID || article._id;
    const userId = getUserId();
    const currentReadLaterState = readLaterItems[articleId] || article.isReadLater;
    const newReadLaterState = !currentReadLaterState;

    // Optimistic update
    setReadLaterItems((prev) => ({ ...prev, [articleId]: newReadLaterState }));
    updateArticleState(articleId, { isReadLater: newReadLaterState });

    try {
      if (currentReadLaterState) {
        await API.delete(`/readlater/${article.ID}`, {
          params: { userId }
        });
      } else {
        await API.post('/readlater', {
          userId,
          articleId: article.ID
        });
      }
      showToast(newReadLaterState ? 'Added to reading list' : 'Removed from reading list', 'success');
    } catch (error) {
      // Revert on error
      setReadLaterItems((prev) => ({ ...prev, [articleId]: currentReadLaterState }));
      updateArticleState(articleId, { isReadLater: currentReadLaterState });
      showToast('Error updating reading list', 'error');
    }
  };

  // Handle sort change for all view modes
  const handleSortChange = (e) => {
    const [key, direction] = e.target.value.split(':');
    setCardViewSortConfig({ key, direction });
  };

  // Handle sort change from DataTable (when it calls with an object instead of event)
  const handleSortConfigChange = (sortConfig) => {
    setCardViewSortConfig(sortConfig);
  };

  // Sort articles for all view modes
  const sortedArticles = useMemo(() => {
    const sorted = [...articles].sort((a, b) => {
      const { key, direction } = cardViewSortConfig;
      let aVal = a[key];
      let bVal = b[key];

      // Handle date fields
      if (key === 'createdAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string' && typeof bVal === 'string') {
        // Handle string fields
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (direction === 'ascending') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  }, [articles, cardViewSortConfig]);

  // welcome + new articles
  useEffect(() => {
    const user = getCurrentUser();
    setUserName(user?.name || '');
    if (user && sessionStorage.getItem('welcomeShown') !== '1') {
      setShowWelcome(true);
      sessionStorage.setItem('welcomeShown', '1');
    }
  }, []);
  const user = getCurrentUser();
  const lastLogin = user?.lastLogin ? new Date(user.lastLogin) : null;

  useEffect(() => {
    const u = getCurrentUser();
    if (!u || !u.lastLogin || !articles.length) return;
    const last = new Date(u.lastLogin);
    setNewArticlesCount(articles.filter((a) => a.createdAt && new Date(a.createdAt) > last).length);
  }, [articles]);

  // prefs
  const { preferences: filterPreferences } = React.useContext(FilterPreferencesContext);
  useEffect(() => {
    if (!filterPreferences || Object.keys(filterPreferences).length === 0) {
      return;
    }

    const overrides = Object.entries(filterPreferences).filter(([, value]) => Array.isArray(value) && value.length > 0);
    if (overrides.length === 0) {
      return;
    }

    const currentFilters = filtersRef.current;
    const next = sanitizeDatabaseFilters({
      ...currentFilters,
      ...Object.fromEntries(overrides),
    });

    if (!areFiltersEqual(currentFilters, next)) {
      setFilters(next);
      syncFiltersToUrl(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPreferences]);

  /* ------------------ render ------------------ */
  return (
    <div className="database-page">
      <DatabaseFilterModal
        open={defineModalOpen}
        options={filterOptions}
        initialFilters={filters}
        onClose={handleCloseDefineFilter}
        onSave={handleSaveDefinedFilter}
        saving={savingDefinedFilter}
        errorMessage={defineModalError}
      />

      <WelcomeModal
        open={showWelcome}
        userName={userName}
        newCount={newArticlesCount}
        onClose={() => setShowWelcome(false)}
      />

      <PatentUpgradeModal
        isOpen={showPatentUpgradeModal}
        onClose={() => setShowPatentUpgradeModal(false)}
      />

      {/* FiltersSidebar is rendered in DashboardLayout, not here */}

      <div className="database-layout">
        <div className="database-sticky-container">
          {renderSavedFiltersPanel()}
        </div>

        <section className="database-results-section" style={{ position: 'relative' }}>
          <TrialExpiredOverlay section="results" />
          {isLoading ? (
            <div className="loading-indicator">Loading data...</div>
          ) : error ? (
            <div className="error-message">
              Error: {error} <button onClick={refreshData}>Retry</button>
            </div>
          ) : viewMode === 'card' ? (
            <>
              <div className={`card-grid ${sortedArticles.length <= 2 ? 'card-grid-limited' : ''}`}>
                {sortedArticles.length === 0 ? (
                  <div className="no-results">No results found</div>
                ) : (
                  sortedArticles.map((article) => (
                    <ArticleCard
                      key={article.ID || article._id}
                      article={article}
                      isFavorite={article.isFavorite || favorites[article.ID || article._id]}
                      isReadLater={article.isReadLater || readLaterItems[article.ID || article._id]}
                      onFavorite={handleFavoriteToggle}
                      onReadLater={handleReadLaterToggle}
                      onClick={() => setSelectedArticle(article)}
                      showTags={displayTags}
                      readArticles={readArticles}
                      lastLogin={lastLogin}
                      onPatentLinkRestricted={() => setShowPatentUpgradeModal(true)}
                      tagStylePreference={effectiveTagStylePreference}
                    />
                  ))
                )}
              </div>

              {selectedArticle && (
                <ArticleDetail
                  article={selectedArticle}
                  onClose={() => setSelectedArticle(null)}
                  onFavoriteChange={(id, fav) => {
                    setFavorites((f) => ({ ...f, [id]: fav }));
                    updateArticleState(id, { isFavorite: fav });
                    showToast(fav ? 'Add to favorites' : 'Remove from favorites', 'success');
                  }}
                  onReadLaterChange={(id, rl) => {
                    setReadLaterItems((f) => ({ ...f, [id]: rl }));
                    updateArticleState(id, { isReadLater: rl });
                    showToast(rl ? 'Add to reading list' : 'Remove from reading list', 'success');
                  }}
                  onRead={handleMarkAsRead}
                />
              )}
            </>
          ) : (
            <DataTable
              data={sortedArticles}
              onRefresh={refreshData}
              showTags={displayTags}
              tagStylePreference={effectiveTagStylePreference}
              activeFilters={filters}
              onRemoveFilter={handleRemoveFilter}
              savedFiltersPanel={null}
              density={density}
              onDensityChange={setDensity}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              onFavorite={handleFavoriteToggle}
              onReadLater={handleReadLaterToggle}
              onPatentLinkRestricted={() => setShowPatentUpgradeModal(true)}
              renderHeaderInParent={true}
              sortConfig={cardViewSortConfig}
              onSortChange={handleSortConfigChange}
            />
          )}
          {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </section>
      </div>
    </div>
  );
};

export default DatabasePage;





